import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { DeclarativeLayoutRenderer } from './DeclarativeLayoutRenderer';
import { CONSTRUCTION_MEETING_MINUTES_SECTION_CONFIG } from './sections.config';

interface ConstructionMeetingMinutesSectionProps {
  magicReportId: string;
}

const COMMENT_FIELD = 'ConstructionMeetingMinutesComment';
const PLACEHOLDER = 'XXXXXXX';
export const DEFAULT_CONSTRUCTION_MEETING_MINUTES_COMMENT =
  'We did not attend the Construction Progress Meeting this month and were not provided with the meeting notes. We will request that we receive a copy of the meeting minutes before our next review.';

const ConstructionMeetingMinutesSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ handleFieldChange, getField, onBook }) => {
  // Comment carries forward month-to-month on the backend, so it's only ever auto-populated
  // once, the first time this field has no value at all
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    if (!getField(COMMENT_FIELD)?.value) {
      handleFieldChange(COMMENT_FIELD, { value: DEFAULT_CONSTRUCTION_MEETING_MINUTES_COMMENT });
    }
  }, [getField, handleFieldChange]);

  const handleRefreshComment = () => {
    handleFieldChange(COMMENT_FIELD, { value: DEFAULT_CONSTRUCTION_MEETING_MINUTES_COMMENT });
  };

  const layout = useMemo(
    () => ({
      singleColumn: [
        {
          fieldName: COMMENT_FIELD,
          type: 'text-with-refresh' as const,
          label: '',
          errorIfContains: PLACEHOLDER,
          onRefresh: handleRefreshComment,
        },
      ],
    }),
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DeclarativeLayoutRenderer layout={layout} getField={getField} handleFieldChange={handleFieldChange} onBook={onBook} />
    </Box>
  );
};

export const ConstructionMeetingMinutesSection: React.FC<ConstructionMeetingMinutesSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={CONSTRUCTION_MEETING_MINUTES_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <ConstructionMeetingMinutesSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
